import { useEffect, useState } from 'react';
import type { MedplumClient } from '@medplum/core';
import { getZoomSeatUsage } from '@/lib/adminActionsService';
import { zoomSeatUsageSchema, type ZoomSeatUsage } from '@/schemas/zoom.schema';

export interface UseZoomSeatUsageResult {
  usage: ZoomSeatUsage | null;
  loading: boolean;
  error: string | null;
}

export function useZoomSeatUsage(
  medplum: MedplumClient,
  enabled: boolean,
): UseZoomSeatUsageResult {
  const [usage, setUsage] = useState<ZoomSeatUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setUsage(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getZoomSeatUsage(medplum)
      .then((result) => {
        if (cancelled) return;
        const parsed = zoomSeatUsageSchema.safeParse(result);
        if (!parsed.success) {
          setError('Unexpected seat usage response from server.');
          setUsage(null);
          return;
        }
        setUsage(parsed.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setUsage(null);
        setError(err instanceof Error ? err.message : 'Failed to load Zoom seat usage.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, enabled]);

  return { usage, loading, error };
}
